/**
 * Content Manager Service Helpers
 * Helper functions for Content Manager API requests
 */

import { buildQueryString } from '../config/api.config';
import type { ContentManagerQueryOptions } from './content-manager.types';

/**
 * Recursively flattens a filters object into Strapi bracket notation,
 * e.g. { name: { $containsi: 'x' } } -> params['filters[name][$containsi]'] = 'x'
 */
function flattenFilters(obj: any, prefix: string, params: Record<string, string>): void {
  if (obj === null || obj === undefined) return;
  if (typeof obj !== 'object') {
    params[prefix] = String(obj);
    return;
  }
  if (Array.isArray(obj)) {
    obj.forEach((value, index) => flattenFilters(value, `${prefix}[${index}]`, params));
    return;
  }
  Object.keys(obj).forEach((key) => {
    flattenFilters(obj[key], `${prefix}[${key}]`, params);
  });
}

/**
 * Build query parameters for Content Manager API
 */
export function buildContentManagerQueryParams(options?: ContentManagerQueryOptions): string {
  if (!options) return '';

  const params: Record<string, string> = {};

  if (options.page) {
    params.page = options.page.toString();
  }

  if (options.pageSize) {
    params.pageSize = options.pageSize.toString();
  }

  if (options.sort) {
    params.sort = options.sort;
  }

  if (options.q) {
    params._q = options.q;
  }

  if (options.filters) {
    // Strapi expects bracket notation (filters[name][$containsi]=x),
    // JSON-stringified values are silently ignored
    flattenFilters(options.filters, 'filters', params);
  }

  if (options.populate) {
    if (Array.isArray(options.populate)) {
      options.populate.forEach((field, index) => {
        params[`populate[${index}]`] = field;
      });
    } else {
      params.populate = options.populate;
    }
  }

  return buildQueryString(params);
}

/**
 * Parse error response from Content Manager API and throw with status/details
 */
export async function parseContentManagerError(
  response: Response,
  defaultMessage: string
): Promise<never> {
  let errorMessage = defaultMessage;
  let errorData: any = null;

  try {
    errorData = await response.json();
    errorMessage = errorData.error?.message || errorData.message || errorMessage;
  } catch {
    errorMessage = response.statusText || errorMessage;
  }

  console.error('❌ [Content Manager] Error:', {
    status: response.status,
    message: errorMessage,
    data: errorData,
  });

  const error: any = new Error(errorMessage);
  error.status = response.status;
  error.statusText = response.statusText;
  error.data = errorData;

  throw error;
}
